import { useState } from "react";
import { Navigate, useNavigate, useRouterState } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { Logo } from "@/components/molecules/Logo";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const {
    session,
    isAdmin,
    requiresTotp,
    mfaFeatureEnabled,
    signIn,
    verifyTotp,
    cancelTotpChallenge,
    accessDeniedMessage,
    clearAccessDeniedMessage,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const search = (location.search as Record<string, unknown>) ?? {};
  const reason = typeof search.reason === "string" ? search.reason : null;

  if (session && isAdmin) {
    return <Navigate to="/overview" search={{ period: "30d" }} replace />;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clearAccessDeniedMessage();
    setError(null);
    setIsSubmitting(true);

    const result = await signIn(email.trim(), password, {
      rememberDevice,
    });
    if (result.error) {
      setError(result.error);
      setIsSubmitting(false);
      return;
    }

    if (result.requiresTotp) {
      setIsSubmitting(false);
      return;
    }

    await navigate({ to: "/overview", search: { period: "30d" } });
  }

  async function handleVerifyTotp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const result = await verifyTotp(totpCode);
    if (result.error) {
      setError(result.error);
      setIsSubmitting(false);
      return;
    }

    await navigate({ to: "/overview", search: { period: "30d" } });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Logo variant="vertical" className="mb-4 justify-center" />
          <CardTitle>Centro de Comando</CardTitle>
          <CardDescription>
            {requiresTotp
              ? "Confirme o codigo do autenticador para concluir o login"
              : "Acesso restrito a administradores"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={requiresTotp ? handleVerifyTotp : handleSubmit}>
            {((reason === "forbidden" && !requiresTotp) || accessDeniedMessage) && (
              <p className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {accessDeniedMessage ?? "Acesso negado para este usuario."}
              </p>
            )}
            {error && (
              <p className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            {requiresTotp ? (
              <>
                <div className="space-y-2">
                  <label htmlFor="totp" className="text-sm font-medium">
                    Codigo TOTP (6 digitos)
                  </label>
                  <Input
                    id="totp"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? "Validando..." : "Validar codigo"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={isSubmitting}
                    onClick={() => {
                      void cancelTotpChallenge();
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">
                    Senha
                  </label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>

                {mfaFeatureEnabled && (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Checkbox
                      checked={rememberDevice}
                      onCheckedChange={(checked) => setRememberDevice(checked === true)}
                    />
                    <span>Manter logado neste dispositivo</span>
                  </label>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Entrando..." : "Entrar"}
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
